import React, { useMemo } from 'react';
import { Box, Grid } from '@mui/material';
import {
  Event as EventIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import { MeetingsMetrics, CHART_COLORS } from '../../types/dashboard.types';
import MetricCard from './MetricCard';
import ChartCard from './ChartCard';
import SectionHeader from './SectionHeader';
import EmptyState from './EmptyState';
import TopExhibitorsTable from './TopExhibitorsTable';

export interface MeetingsMetricsSectionProps {
  data: MeetingsMetrics;
  loading?: boolean;
}

const getStatusColor = (status: string): string => {
  const statusLower = status.toLowerCase();
  if (statusLower.includes('complete')) return CHART_COLORS.success;
  if (statusLower.includes('cancel')) return CHART_COLORS.error;
  if (statusLower.includes('schedule')) return CHART_COLORS.primary;
  if (statusLower.includes('pending')) return CHART_COLORS.warning;
  return CHART_COLORS.purple;
};

const MeetingsMetricsSection: React.FC<MeetingsMetricsSectionProps> = ({
  data,
  loading = false,
}) => {
  const statusData = useMemo(
    () =>
      data.meetingsByStatus.map((item, index) => ({
        name: item.status.charAt(0).toUpperCase() + item.status.slice(1),
        value: item.count,
        color: getStatusColor(item.status),
      })),
    [data.meetingsByStatus]
  );

  const dayData = useMemo(
    () =>
      data.meetingsByDay.map((item, index) => ({
        name: `Day ${item.dayCode}`,
        count: item.count,
        fill: CHART_COLORS.palette[index % CHART_COLORS.palette.length],
      })),
    [data.meetingsByDay]
  );

  const topExhibitorsData = useMemo(
    () =>
      data.topExhibitorsByMeetings.map(item => ({
        exhibitorId: item.exhibitorId,
        exhibitorName: item.exhibitorName,
        count: item.meetingCount,
      })),
    [data.topExhibitorsByMeetings]
  );

  const totalMeetings = statusData.reduce((sum, item) => sum + item.value, 0);
  const hasStatusData = statusData.length > 0;
  const hasDayData = dayData.length > 0;

  return (
    <Box>
      <SectionHeader title="Meetings" icon={<EventIcon />} />

      <Grid container spacing={3}>
        {/* Metric Cards Row */}
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Total Meetings"
            value={totalMeetings}
            icon={<EventIcon />}
            color={CHART_COLORS.primary}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Completion Rate"
            value={data.completionRate}
            icon={<CheckCircleIcon />}
            color={CHART_COLORS.success}
            format="percent"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="No-Show Rate"
            value={data.noShowRate}
            icon={<CancelIcon />}
            color={CHART_COLORS.error}
            format="percent"
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <MetricCard
            title="Days with Meetings"
            value={dayData.length}
            icon={<CalendarIcon />}
            color={CHART_COLORS.purple}
            loading={loading}
          />
        </Grid>

        {/* Charts Row */}
        <Grid item xs={12} md={6}>
          <ChartCard
            title="Meetings by Status"
            subtitle="Distribution by meeting status"
            height={280}
            loading={loading}
          >
            {!hasStatusData ? (
              <EmptyState message="No meeting status data available" height={250} />
            ) : (
              <>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={statusData}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) =>
                        `${name}: ${(percent * 100).toFixed(0)}%`
                      }
                    >
                      {statusData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: 2,
                  }}
                >
                  {statusData.map((item, index) => (
                    <Box
                      key={index}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          borderRadius: '50%',
                          backgroundColor: item.color,
                        }}
                      />
                      <Box sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        {item.name}: {item.value}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </ChartCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ChartCard
            title="Meetings by Day"
            subtitle="Distribution across event days"
            height={280}
            loading={loading}
          >
            {!hasDayData ? (
              <EmptyState message="No daily meeting data available" height={250} />
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={dayData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" stroke="#6b7280" />
                  <YAxis stroke="#6b7280" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    }}
                  />
                  <Bar dataKey="count" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </ChartCard>
        </Grid>

        {/* Top Exhibitors Table */}
        <Grid item xs={12}>
          <TopExhibitorsTable
            title="Top 10 Exhibitors by Meetings"
            data={topExhibitorsData}
            countLabel="Meetings"
            emptyMessage="No meeting data for exhibitors"
            maxItems={10}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default MeetingsMetricsSection;
